/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { makeMockResponse } from '../mockUtils';
import { delayedResponse } from '../companies/companiesMock';
import { StoreType } from '../stores/storesClientTypings';
import {
    CashRegisterModelShortDto,
    FirmwareVersionDTO,
    FiscalDriverVersionDto,
    OperatorShortDto,
    StoreLowerActivity,
    StoreMainActivity,
    StoreMiddleActivity,
    StoreTypeResource,
    TaxationTypeDTO,
    TaxAuthorityDTO,
    UserShortDto,
} from './shortListsClient.typings';
import {
    cashRegisterModelShortDtoFactory,
    cityDtoFactory,
    firmwareVersionFactory,
    fiscalDriverVersionFactory,
    lowerActivitiesDtoFactory,
    mainActivitiesDtoFactory,
    middleActivitiesDtoFactory,
    operatorShortDtoFactory,
    regionDtoFactory,
    storeTypeFactory,
    taxationTypeFactory,
    taxAuthorityFactory,
    userShortDtoFactory,
} from './shortListsFactories';
import { TAXATION_TYPES } from '../companies/companiesClient.typings';

const CASH_REGISTERS_MODELS_URL: RegExp = /\/cashRegisterModels\/short$/;
const OPERATORS_URL: RegExp = /\/operators\/short$/;
const USERS_URL: RegExp = /\/users\/short$/;
const STORE_TYPES_URL: RegExp = /\/stores\/types$/;
const FIRMWARE_VERSIONS_URL: RegExp = /\/firmwareVersions$/;
const FISCAL_DRIVER_VERSIONS_URL: RegExp = /\/fiscalDriverVersions$/;
const TAX_AUTHORITIES_URL: RegExp = /\/taxAuthorities$/;
const TAXATION_TYPES_URL: RegExp = /\/taxationTypes$/;
const CITIES_URL: RegExp = /\/cities$/;
const REGIONS_URL: RegExp = /\/regions$/;
const MAIN_ACTIVITIES_LIST_URL: RegExp = /\/activities\/main$/;
const MIDDLE_ACTIVITIES_LIST_URL: RegExp = /\/activities\/middle$/;
const LOWER_ACTIVITIES_LIST_URL: RegExp = /\/activities\/lower$/;

export function addShortListsMockApi(mockAdapter: MockAdapter): void {
    mockAdapter.onGet(CASH_REGISTERS_MODELS_URL).reply((config) => {
        const data: CashRegisterModelShortDto[] = cashRegisterModelShortDtoFactory.list({ count: 100 });
        cashRegisterModelShortDtoFactory.resetCount();
        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(OPERATORS_URL).reply((config) => {
        const data: OperatorShortDto[] = operatorShortDtoFactory.list({ count: 22 });
        operatorShortDtoFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(USERS_URL).reply((config) => {
        const data: UserShortDto[] = userShortDtoFactory.list({ count: 30 });
        userShortDtoFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 200);
    });

    mockAdapter.onGet(STORE_TYPES_URL).reply((config) => {
        const data: StoreTypeResource[] = storeTypeFactory.list({ count: Object.values(StoreType).length });
        storeTypeFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 200);
    });

    mockAdapter.onGet(FIRMWARE_VERSIONS_URL).reply((config) => {
        const data: FirmwareVersionDTO[] = firmwareVersionFactory.list({ count: 10 });
        firmwareVersionFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(FISCAL_DRIVER_VERSIONS_URL).reply((config) => {
        const data: FiscalDriverVersionDto[] = fiscalDriverVersionFactory.list({ count: 100 });
        firmwareVersionFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(TAX_AUTHORITIES_URL).reply((config) => {
        const data: TaxAuthorityDTO[] = taxAuthorityFactory.list({ count: 50 });
        taxAuthorityFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(TAXATION_TYPES_URL).reply((config) => {
        const data: TaxationTypeDTO[] = taxationTypeFactory.list({ count: TAXATION_TYPES.length });
        taxationTypeFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(CITIES_URL).reply((config) => {
        const data = cityDtoFactory.list({ count: 1000 });

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(REGIONS_URL).reply((config) => {
        const data = regionDtoFactory.list({ count: 100 });

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(MAIN_ACTIVITIES_LIST_URL).reply((config) => {
        const data = mainActivitiesDtoFactory.list({ count: Object.values(StoreMainActivity).length + 4 });

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(MIDDLE_ACTIVITIES_LIST_URL).reply((config) => {
        const data = middleActivitiesDtoFactory.list({ count: Object.values(StoreMiddleActivity).length + 4 });

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(LOWER_ACTIVITIES_LIST_URL).reply((config) => {
        const data = lowerActivitiesDtoFactory.list({ count: Object.values(StoreLowerActivity).length + 4 });

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });
}
